import { Button } from "primereact/button";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { ClaimBulkTarget, IClaim } from "../../types/claim";
import { claimCount } from "./bulkActionShared";
import { BulkEditPayerModal } from "./BulkEditPayerModal";
import { BulkEditStatusModal } from "./BulkEditStatusModal";
import { BulkReprocessModal } from "./BulkReprocessModal";
import { BulkRunAiAnalysisModal } from "./BulkRunAiAnalysisModal";

interface IBulkClaimActionsBarProps {
  selectedClaims: IClaim[];
  /** CLE-3035: every claim matching the current filters is selected, not just the rows on this page. */
  selectAllMatching: boolean;
  /** How many claims match the current filters (the list's totalCount). */
  matchingCount: number;
  /** How many rows the current page shows. */
  pageCount: number;
  /** The `GET /claims` query-string record for the current filters (no page / sort) — the "all matching" target. */
  filter: Record<string, string>;
  onSelectAllMatching: () => void;
  onClearSelection: () => void;
  accountId: string;
  /** Called after a bulk action is accepted — typically clears selection in the parent. */
  onActionSuccess: () => void;
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  flex-wrap: wrap;
`;

const Count = styled.span`
  font-weight: 600;
  color: #1565c0;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #1565c0;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 0.875rem;
  &:hover {
    color: #0d47a1;
  }
`;

const Note = styled.span`
  color: #37474f;
  font-size: 0.875rem;
`;

const Spacer = styled.div`
  flex: 1;
`;

/**
 * CLE-2172: bulk action bar that appears above the claims list when one or more
 * rows are selected. Four actions: update matched payer, update resolution
 * status, reprocess, and run AI analysis (CLE-2551). Each opens its own confirm
 * modal.
 *
 * CLE-3035: no cap, and selection can reach past the page (the Gmail pattern):
 * once every row on the page is selected and more claims match the filters, a
 * link selects all of them; the actions then target the filters themselves
 * (`{ filter }`) instead of a list of ids, so the server resolves exactly the
 * rows the list would show.
 */
export function BulkClaimActionsBar({
  selectedClaims,
  selectAllMatching,
  matchingCount,
  pageCount,
  filter,
  onSelectAllMatching,
  onClearSelection,
  accountId,
  onActionSuccess,
}: IBulkClaimActionsBarProps) {
  const [payerModalOpen, setPayerModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [aiAnalysisModalOpen, setAiAnalysisModalOpen] = useState(false);

  const pageFullySelected =
    pageCount > 0 && selectedClaims.length === pageCount;
  const count = selectAllMatching ? matchingCount : selectedClaims.length;
  const target: ClaimBulkTarget = useMemo(
    () =>
      selectAllMatching
        ? { filter }
        : { claimIds: selectedClaims.map((c) => c.id) },
    [selectAllMatching, filter, selectedClaims]
  );

  return (
    <Bar>
      <Count>{claimCount(count)} selected</Count>
      <LinkButton type="button" onClick={onClearSelection}>
        Clear selection
      </LinkButton>
      {selectAllMatching && (
        <Note>
          All {matchingCount.toLocaleString("en-US")} claims matching the
          current filters are selected.
        </Note>
      )}
      {!selectAllMatching && pageFullySelected && matchingCount > pageCount && (
        <Note>
          All {pageCount.toLocaleString("en-US")} claims on this page are
          selected.{" "}
          <LinkButton type="button" onClick={onSelectAllMatching}>
            Select all {matchingCount.toLocaleString("en-US")} claims matching
            the current filters
          </LinkButton>
        </Note>
      )}
      <Spacer />
      <Button
        label="Update Matched Payer"
        icon="pi pi-pencil"
        className="p-button-sm"
        onClick={() => setPayerModalOpen(true)}
      />
      <Button
        label="Update Status"
        icon="pi pi-flag"
        className="p-button-sm p-button-outlined"
        onClick={() => setStatusModalOpen(true)}
      />
      <Button
        label="Reprocess"
        icon="pi pi-refresh"
        className="p-button-sm p-button-outlined"
        onClick={() => setReprocessModalOpen(true)}
      />
      <Button
        label="Run AI Analysis"
        icon="pi pi-sparkles"
        className="p-button-sm p-button-outlined"
        onClick={() => setAiAnalysisModalOpen(true)}
      />

      <BulkEditPayerModal
        visible={payerModalOpen}
        onHide={() => setPayerModalOpen(false)}
        target={target}
        count={count}
        accountId={accountId}
        onSuccess={() => {
          setPayerModalOpen(false);
          onActionSuccess();
        }}
      />

      <BulkEditStatusModal
        visible={statusModalOpen}
        onHide={() => setStatusModalOpen(false)}
        target={target}
        count={count}
        onSuccess={() => {
          setStatusModalOpen(false);
          onActionSuccess();
        }}
      />

      <BulkReprocessModal
        visible={reprocessModalOpen}
        onHide={() => setReprocessModalOpen(false)}
        target={target}
        count={count}
        onSuccess={() => {
          setReprocessModalOpen(false);
          onActionSuccess();
        }}
      />

      <BulkRunAiAnalysisModal
        visible={aiAnalysisModalOpen}
        onHide={() => setAiAnalysisModalOpen(false)}
        target={target}
        count={count}
        onSuccess={() => {
          setAiAnalysisModalOpen(false);
          onActionSuccess();
        }}
      />
    </Bar>
  );
}
